import {
  ICtaTemplateApi,
  ICtaTemplateApiDeliverables,
} from '@campaign-form/form-fields/creators-and-ctas/cta-dialog.interface';
import { EExecutionType } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/execution-step/execution-list/execution/execution.interface';
import { EAnalyticsStatus, EMatchingType, EProcessStatus } from '@enums/campaign-analytics.enum';
import {
  ICtaResultApi,
  IExpectedKpiApi,
  IKpiAnalyticsApi,
  IPlannedCtaApi,
  IPlannedExecutionApi,
  TKpiAnalyticsKey,
} from '@interfaces/campaign-analytics-api.interface';
import { IAnalyticsNode, ICampaignAnalytics, TExpectedKpi } from '@interfaces/campaign-analytics.interface';
import { TBarTone } from '@shared/components/slot-bar/slot-bar.interface';
import { TTileContentStatus } from '@shared/components/social-content-tile/social-content-tile.interface';
import { isCtaResultCounted } from '@shared/components/social-content-tile/social-content-tile.util';
import { IDeepLinkGroupInfo } from '../campaign-details-deep-link/campaign-details-deep-link.interface';
import { ICampaignAnalyticsFilter } from '../campaign-details.interface';
import { ICampaignExportConfig } from '../campaign-export-config/campaign-export-config.interface';
import { isAnalyticsNodeInGroup } from '../social-content-tiles-list/social-content-tiles-list.util';
import {
  EGoalsLens,
  EGoalsSortKey,
  IGoalsCell,
  IGoalsCellLens,
  IGoalsCellLink,
  IGoalsColumn,
  IGoalsDeliverableRow,
  IGoalsDeliverablesLinkFilters,
  IGoalsFooter,
  IGoalsFooterSummary,
  IGoalsKpiRow,
  IGoalsMatrix,
  IGoalsMatrixParams,
  IGoalsNote,
  IGoalsRow,
  IGoalsSlot,
  IGoalsSlotDeliverable,
  IGoalsSlotKpi,
  IGoalsSort,
  IGoalsTemplateKpi,
  TGoalsCellKind,
  TGoalsDeliverablesLinkRow,
  TGoalsDeliverablesLinkScope,
  TGoalsFrequency,
  TGoalsKpiState,
} from './campaign-details-goals.interface';

interface IGoalsContentMatch {
  ctaResult: ICtaResultApi;
  node: IAnalyticsNode;
}

interface IGoalsCellInput {
  creatorId: string;
  ctaId: string;
  ctaName: string;
  /** The contents an Optional cell counts: every evaluation of this group, including bonus counted elsewhere. */
  evaluations: IGoalsContentMatch[];
  matches: IGoalsContentMatch[];
  placeholders: IAnalyticsNode[];
  plannedCta: IPlannedCtaApi | null;
}

interface IGoalsExecutionCount {
  executionCountTarget?: number | null;
  executionType: EExecutionType | null;
}

export const EXPECTED_KPI_TO_ANALYTICS_KEY: Record<TExpectedKpi, TKpiAnalyticsKey> = {
  averageViewers: 'avgViewers',
  comments: 'comments',
  engagementRate: 'engagementRate',
  engagementRateViews: 'engagementRateViews',
  engagements: 'engagements',
  impressions: 'impressions',
  likes: 'likes',
  mediaValue: 'mediaValue',
  peakViewers: 'peakViewers',
  predictedImpressions: 'predictedImpressions',
  quotes: 'quotes',
  reach: 'reach',
  retweets: 'retweets',
  shares: 'shares',
  sponsoredAverageViewers: 'sponsoredAvgViewers',
  sponsoredPeakViewers: 'sponsoredPeakViewers',
  sponsoredTotalWatchedHours: 'sponsoredTotalWatchedHours',
  totalWatchedHours: 'totalWatchedHours',
  views: 'views',
};

const TRACKING_TEMPLATE_KEYS = new Set<keyof ICtaTemplateApiDeliverables>(['audioTrackings', 'textTrackings']);

const TEMPLATE_DELIVERABLE_KEYS: (keyof ICtaTemplateApiDeliverables)[] = [
  'audioTrackings',
  'captions',
  'chatbotMultis',
  'chatbots',
  'contents',
  'customs',
  'descriptions',
  'gamePlays',
  'hashTags',
  'mentions',
  'messages',
  'productPlacements',
  'showcases',
  'talkingPointAis',
  'talkingPoints',
  'textTrackings',
  'titles',
  'visuals',
];

const STATUS_TONE: Record<EAnalyticsStatus, TBarTone> = {
  [EAnalyticsStatus.ok]: 'success',
  [EAnalyticsStatus.problem]: 'warning',
  [EAnalyticsStatus.failed]: 'error',
  [EAnalyticsStatus.processing]: 'processing',
};

const KPI_STATE_TONE: Record<TGoalsKpiState, TBarTone> = {
  below: 'error',
  noData: 'hatched',
  partial: 'warning',
  reached: 'success',
};

/** Shared Reports filter KPI results with the content-level status set: reached = ok, below = problem, no data = failed. */
const KPI_STATE_EXPORT_STATUS: Record<TGoalsKpiState, EAnalyticsStatus> = {
  below: EAnalyticsStatus.problem,
  noData: EAnalyticsStatus.failed,
  partial: EAnalyticsStatus.problem,
  reached: EAnalyticsStatus.ok,
};

/** Contents-tab filter behind the "still to deliver" / "not delivered" notes. */
const OPEN_SLOT_MATCHING_TYPES = [EMatchingType.planned, EMatchingType.missing];
const DELIVERED_MATCHING_TYPES = [EMatchingType.matched];

/** Deliverables lens: the content statuses a cell counts as "with issues" (Contents-tab filter values). */
const GOALS_ISSUE_CONTENT_STATUSES: TTileContentStatus[] = ['partiallyFulfilled', 'notFulfilled'];

const CONTENT_STATUS_API: Record<TTileContentStatus, EAnalyticsStatus> = {
  fulfilled: EAnalyticsStatus.ok,
  notFulfilled: EAnalyticsStatus.failed,
  partiallyFulfilled: EAnalyticsStatus.problem,
};

/** What a group's blocks count (bonus contents are extras): the Deliverables-tab links select these. */
export const GOALS_COUNTED_MATCHING_TYPES = [EMatchingType.matched, EMatchingType.planned, EMatchingType.missing];

/** Every matching type but "unrelated": the Contents tab's default selection. */
export const GOALS_RELATED_MATCHING_TYPES = [
  EMatchingType.matched,
  EMatchingType.bonus,
  EMatchingType.planned,
  EMatchingType.missing,
];

const FOOTER_TONES: TBarTone[] = ['success', 'warning', 'error', 'processing', 'hatched'];

const COUNTED_EXECUTION_EXCLUDED = new Set<EExecutionType | null>([
  EExecutionType.always,
  EExecutionType.noExpectations,
]);

const DELIVERABLE_STATUS_LABEL: Record<EAnalyticsStatus, string> = {
  [EAnalyticsStatus.ok]: 'shared.statuses.fulfilled',
  [EAnalyticsStatus.problem]: 'shared.statuses.partiallyFulfilled',
  [EAnalyticsStatus.failed]: 'shared.statuses.notFulfilled',
  [EAnalyticsStatus.processing]: 'shared.statuses.processing',
};

const KPI_STATE_LABEL: Record<TGoalsKpiState, string> = {
  below: 'pages.campaignDetails.goals.legend.allBelow',
  noData: 'pages.campaignDetails.goals.legend.noData',
  partial: 'pages.campaignDetails.goals.legend.someBelow',
  reached: 'pages.campaignDetails.goals.legend.allReached',
};

/** One target's result in an expanded KPI row (a single target is never "partial"). */
const KPI_TARGET_STATE_LABEL: Record<IGoalsSlotKpi['state'], string> = {
  below: 'shared.statuses.kpiGoalsBelowTarget',
  noData: 'pages.campaignDetails.goals.legend.noData',
  reached: 'shared.statuses.kpiGoalsReached',
};

const NOT_DELIVERED_LABEL = 'pages.campaignDetails.goals.legend.notDelivered';

/** A cell note's hover-card link label: delivered-type notes all become "View n delivered". */
const CARD_LINK_KEY: Record<string, string> = {
  allDelivered: 'delivered',
  complete: 'delivered',
  notDelivered: 'notDelivered',
  stillToDeliver: 'stillToDeliver',
  streamsSoFar: 'delivered',
  withIssues: 'issues',
};

/** Translation key describing a slot in the given lens; null when Shared Reports hide its status. */
export function getGoalsSlotLabelKey(slot: IGoalsSlot, lens: EGoalsLens, isHidden: boolean): string | null {
  if (slot.kind === 'free') {
    return NOT_DELIVERED_LABEL;
  }

  if (lens === EGoalsLens.kpis && !slot.hasKpiGoals) {
    return 'pages.campaignDetails.goals.legend.noKpiGoals';
  }

  if (isHidden) {
    return null;
  }

  return lens === EGoalsLens.kpis ? KPI_STATE_LABEL[slot.kpiState!] : DELIVERABLE_STATUS_LABEL[slot.status!];
}

export function getKpiCurrentValue(
  kpiAnalytics: IKpiAnalyticsApi | null | undefined,
  kpiType: TExpectedKpi,
): number | null {
  const analyticsKey = EXPECTED_KPI_TO_ANALYTICS_KEY[kpiType];

  if (!kpiAnalytics || !analyticsKey) {
    return null;
  }

  return (kpiAnalytics as unknown as Record<string, number | null | undefined>)[analyticsKey] ?? null;
}

export function getGoalsKpiKey(kpiType: TExpectedKpi, target: number): string {
  return `${kpiType}:${target}`;
}

export function buildGoalsMatrix(params: IGoalsMatrixParams): IGoalsMatrix {
  const { ctaTemplates, filter, nodes, shell } = params;

  if (!shell) {
    return { columns: [], hasKpiGoals: false, rows: [] };
  }

  const plannedByCreator = getPlannedByCreator(shell, filter);
  const columnIds = getOrderedColumnIds(ctaTemplates, plannedByCreator);
  const { evaluationsByKey, matchesByKey, placeholdersByKey } = indexNodes(nodes);
  const templateById = new Map(ctaTemplates.map((template) => [template.id, template]));
  const firstPlannedCtaById = getFirstPlannedCtaById(plannedByCreator);
  const rows: IGoalsRow[] = [];

  for (const [creatorId, plannedCtas] of plannedByCreator) {
    const creator = shell.creators.get(creatorId)!;
    const cells: Record<string, IGoalsCell> = {};

    for (const ctaId of columnIds) {
      const key = getNodeKey(creatorId, ctaId);

      cells[ctaId] = buildCell(params, {
        creatorId,
        ctaId,
        ctaName: templateById.get(ctaId)?.name ?? firstPlannedCtaById.get(ctaId)?.cta?.name ?? '',
        evaluations: evaluationsByKey.get(key) ?? [],
        matches: matchesByKey.get(key) ?? [],
        placeholders: placeholdersByKey.get(key) ?? [],
        plannedCta: plannedCtas.get(ctaId) ?? null,
      });
    }

    rows.push({
      cells,
      creatorIcon: creator.icon ?? null,
      creatorId,
      creatorName: creator.name,
      planProgress: {
        [EGoalsLens.deliverables]: getPlanProgress(cells, EGoalsLens.deliverables),
        [EGoalsLens.kpis]: getPlanProgress(cells, EGoalsLens.kpis),
      },
    });
  }

  const columns = columnIds.map((ctaId) =>
    buildColumn(params, ctaId, templateById.get(ctaId) ?? null, firstPlannedCtaById.get(ctaId) ?? null, rows),
  );

  return {
    columns,
    hasKpiGoals: columns.some((column) => column.hasKpiGoals),
    rows: sortGoalsRows(rows, { direction: 'asc', key: EGoalsSortKey.planComplete }, EGoalsLens.deliverables),
  };
}

export function sortGoalsRows(rows: IGoalsRow[], sort: IGoalsSort, lens: EGoalsLens): IGoalsRow[] {
  const direction = sort.direction === 'asc' ? 1 : -1;
  const compareNames = (left: IGoalsRow, right: IGoalsRow): number => left.creatorName.localeCompare(right.creatorName);

  return [...rows].sort((left, right) => {
    if (sort.key === EGoalsSortKey.creator) {
      return compareNames(left, right) * direction;
    }

    const progressDiff = getProgressRatio(left, lens) - getProgressRatio(right, lens);

    return progressDiff !== 0 ? progressDiff * direction : compareNames(left, right);
  });
}

/**
 * The sort the rows show, or null. A lens switch keeps the rows in place, so a progress sort holds only in the lens it
 * was computed in; the creator sort holds in both.
 */
export function getGoalsShownSort(sort: IGoalsSort, sortedLens: EGoalsLens, lens: EGoalsLens): IGoalsSort | null {
  return sort.key === EGoalsSortKey.planComplete && sortedLens !== lens ? null : sort;
}

/** Short status line under a cell's bar, as translation keys under `pages.campaignDetails.goals.notes`. */
export function getGoalsCellNotes(cell: IGoalsCell, lens: EGoalsLens): IGoalsNote[] {
  const { issues, isComplete, noData, slotExcluded } = cell.lenses[lens];
  const notes: IGoalsNote[] = [];

  if (lens === EGoalsLens.kpis) {
    const open = cell.slots.filter((slot, index) => slot.kind === 'free' && !slotExcluded[index]).length;

    if (issues) {
      notes.push({ key: 'belowTarget', params: { count: issues } });
    }

    if (noData) {
      notes.push({ key: 'noDataYet', params: { count: noData } });
    }

    if (open) {
      notes.push({ key: 'notDelivered', matchingTypes: OPEN_SLOT_MATCHING_TYPES, params: { count: open } });
    }

    return isComplete && cell.required > 0 ? [{ key: 'allTargetsReached' }] : notes;
  }

  // Nothing delivered yet → nothing to show on the Contents tab, so the note stays plain.
  const delivered = cell.counted ? DELIVERED_MATCHING_TYPES : undefined;

  if (cell.kind === 'everyStream') {
    notes.push({ key: 'streamsSoFar', matchingTypes: delivered, params: { count: cell.counted } });
  } else if (isComplete) {
    return [{ key: 'complete', matchingTypes: delivered }];
  } else if (!cell.free) {
    notes.push({ key: 'allDelivered', matchingTypes: delivered });
  }

  if (cell.free) {
    notes.push({ key: 'stillToDeliver', matchingTypes: OPEN_SLOT_MATCHING_TYPES, params: { count: cell.free } });
  }

  if (issues) {
    notes.push({
      contentStatuses: GOALS_ISSUE_CONTENT_STATUSES,
      key: 'withIssues',
      matchingTypes: DELIVERED_MATCHING_TYPES,
      params: { count: issues },
    });
  }

  return notes;
}

/**
 * Hover-card links of a cell, as keys under `pages.campaignDetails.goals.card`: one "View … in Contents" per note
 * link, plus its extras. In the Deliverables lens a partly delivered cell still leads with "View n delivered" (its
 * notes carry no delivered link), unless every delivered content has issues. A link a Shared Report can't show
 * (hidden status filter or statuses) is left out.
 */
export function getGoalsCellLinks(
  cell: Pick<IGoalsCell, 'counted' | 'extraSlots'>,
  notes: IGoalsNote[],
  exportConfig: Pick<ICampaignExportConfig, 'allowedContentStatuses' | 'showContentsStatusFilter'> | null,
  lens: EGoalsLens,
): IGoalsCellLink[] {
  const links: IGoalsCellLink[] = [];
  const hasDeliveredNote = notes.some(({ key, matchingTypes }) => matchingTypes && CARD_LINK_KEY[key] === 'delivered');

  if (lens === EGoalsLens.deliverables && cell.counted && !hasDeliveredNote) {
    links.push({
      key: getCardLinkKey('delivered', cell.counted),
      link: { matchingTypes: DELIVERED_MATCHING_TYPES },
      params: { count: cell.counted },
    });
  }

  for (const { contentStatuses, key, matchingTypes, params } of notes) {
    if (!matchingTypes) {
      continue;
    }

    const count = params?.['count'] ?? cell.counted;
    const statuses = contentStatuses ? getGoalsLinkContentStatuses(contentStatuses, exportConfig) : undefined;

    if (statuses !== null) {
      links.push({
        key: getCardLinkKey(CARD_LINK_KEY[key], count),
        link: statuses ? { contentStatuses: statuses, matchingTypes } : { matchingTypes },
        params: { count },
      });
    }
  }

  if (cell.extraSlots.length) {
    links.push({
      key: getCardLinkKey('extra', cell.extraSlots.length),
      link: { matchingTypes: [EMatchingType.bonus] },
      params: { count: cell.extraSlots.length },
    });
  }

  // Every delivered content has issues: "View n issues" opens the same list, so "View n delivered" would repeat it.
  const allWithIssues = links.some(({ link, params }) => link.contentStatuses && params['count'] === cell.counted);

  return allWithIssues ? links.filter(({ key }) => key !== getCardLinkKey('delivered', cell.counted)) : links;
}

/** Count 1 has its own wording ("1 issue", German "1 gelieferten Inhalt"). */
function getCardLinkKey(key: string, count: number): string {
  return count === 1 ? `${key}One` : key;
}

/**
 * Width (ch) of a cell's count, one for both lenses ("12/12" and "3/12"), so switching lens doesn't resize the column.
 * Matches beyond the target count too ("10/9").
 */
export function getGoalsCountWidth(cell: Pick<IGoalsCell, 'counted' | 'required'>): number {
  return String(Math.max(cell.counted, cell.required)).length + String(cell.required).length + 1;
}

/** Blocks a group column's bars are sized for: its longest bar, so every row shares one grid. 0 = no bars. */
export function getGoalsColumnCapacity(rows: IGoalsRow[], ctaId: string): number {
  return Math.max(
    0,
    ...rows.map((row) => {
      const cell = row.cells[ctaId];

      return cell && (cell.kind === 'required' || cell.kind === 'everyStream') ? cell.slots.length : 0;
    }),
  );
}

/** KPI-lens header line: the goal itself when there is one, a count (with the list as tooltip) when several. */
export function getGoalsKpiHeaderText(
  goals: string[],
  translate: (key: string, params?: Record<string, string | number>) => string,
): { text: string; tooltip: string } {
  if (!goals.length) {
    return { text: translate('pages.campaignDetails.goals.header.noKpiGoals'), tooltip: '' };
  }

  if (goals.length === 1) {
    const text = translate('pages.campaignDetails.goals.header.kpiGoal', { goal: goals[0] });

    return { text, tooltip: text };
  }

  return {
    text: translate('pages.campaignDetails.goals.header.kpiGoals', { count: goals.length }),
    tooltip: goals.join(' · '),
  };
}

/** Whether a KPI-lens bar shows a grey "no KPI goals" block; groups without any KPI goals show text, not a bar. */
export function hasGoalsKpiBarSlotsWithoutGoals(rows: IGoalsRow[]): boolean {
  return rows.some((row) =>
    Object.values(row.cells).some(
      (cell) =>
        cell.lenses[EGoalsLens.kpis].hasGoals &&
        cell.slots.some((slot) => slot.kind === 'counted' && !slot.hasKpiGoals),
    ),
  );
}

/** Checks the visible rows, so ids kept from rows a filter hides don't count. */
export function areAllGoalsRowsExpanded(rowIds: string[], expandedRowIds: string[]): boolean {
  const expanded = new Set(expandedRowIds);

  return rowIds.length > 0 && rowIds.every((id) => expanded.has(id));
}

const ANY_LINK_FILTER = '*';

/** One key per filter combination a Goals link can ask the Deliverables tab for; a filter left out matches any. */
function getGoalsDeliverablesLinkKey(
  scope: TGoalsDeliverablesLinkScope,
  { creatorId, ctaId, deliverableType }: IGoalsDeliverablesLinkFilters = {},
): string {
  return [scope, creatorId, ctaId, deliverableType].map((value) => value ?? ANY_LINK_FILTER).join('|');
}

/** Whether the Deliverables tab has rows for a link's filters. */
export function hasGoalsDeliverablesLinkRows(
  keys: Set<string>,
  scope: TGoalsDeliverablesLinkScope,
  filters: IGoalsDeliverablesLinkFilters,
): boolean {
  return keys.has(getGoalsDeliverablesLinkKey(scope, filters));
}

/**
 * The content statuses a Contents link may select: only those a Shared Report shows (a hidden one would filter
 * invisibly). Null keeps the link plain: the report hides the status filter, or every requested status.
 */
export function getGoalsLinkContentStatuses(
  statuses: TTileContentStatus[],
  exportConfig: Pick<ICampaignExportConfig, 'allowedContentStatuses' | 'showContentsStatusFilter'> | null,
): TTileContentStatus[] | null {
  if (exportConfig?.showContentsStatusFilter === false) {
    return null;
  }

  const shown = statuses.filter((status) =>
    isAllowed(exportConfig?.allowedContentStatuses, CONTENT_STATUS_API[status]),
  );

  return shown.length ? shown : null;
}

/**
 * The group a Contents link narrows to. Optional comes from each creator's own plan, not the column: an individual
 * group's column follows the template or the first plan. Per creator and from every plan, not only the creators the
 * Scope shows now, so it stays right once the creator chip is removed or the Scope widens.
 */
export function getGoalsLinkGroup(
  shell: ICampaignAnalytics | null,
  columns: IGoalsColumn[],
  ctaId: string,
): IDeepLinkGroupInfo {
  const plannedByCreator = shell ? [...getPlannedByCreator(shell, null)] : [];

  return {
    id: ctaId,
    name: columns.find((column) => column.ctaId === ctaId)?.name ?? '',
    optionalCreatorIds: new Set(
      plannedByCreator
        .filter(([, plannedCtas]) => getCellKind(plannedCtas.get(ctaId) ?? null) === 'optional')
        .map(([creatorId]) => creatorId),
    ),
  };
}

/**
 * Deliverables-lens footer line on the Contents tab, all creators of the group. Fulfilled contents once there are
 * some; while there are none, "n still to deliver" (the label says what opens) with its planned and missing
 * contents; else "0 of n fulfilled". The link is null (plain text) when the tab would have nothing to show.
 */
export function getGoalsFooterSummary(
  ctaId: string,
  footer: IGoalsFooter,
  nodes: IAnalyticsNode[] | null,
  exportConfig: Pick<ICampaignExportConfig, 'allowedContentStatuses' | 'showContentsStatusFilter'> | null,
): IGoalsFooterSummary {
  if (footer.fulfilled > 0 || !footer.open) {
    const contentStatuses = footer.fulfilled > 0 ? getGoalsLinkContentStatuses(['fulfilled'], exportConfig) : null;

    return {
      labelKey: 'pages.campaignDetails.goals.footer.fulfilled',
      link: contentStatuses ? { contentStatuses, matchingTypes: DELIVERED_MATCHING_TYPES } : null,
      params: { done: footer.fulfilled, total: footer.required },
    };
  }

  const matchingTypes = OPEN_SLOT_MATCHING_TYPES.filter((type) =>
    (nodes ?? []).some((node) => node.executionStatus === type && isAnalyticsNodeInGroup(node, ctaId)),
  );

  return {
    labelKey: 'pages.campaignDetails.goals.notes.stillToDeliver',
    link: matchingTypes.length ? { matchingTypes } : null,
    params: { count: footer.open },
  };
}

/** Each of the row's filter values either set or left out, in every combination. */
function getLinkFilterCombinations(row: TGoalsDeliverablesLinkRow, ctaId: string): IGoalsDeliverablesLinkFilters[] {
  const values: IGoalsDeliverablesLinkFilters = {
    creatorId: row.creatorId,
    ctaId,
    deliverableType: row.deliverableType,
  };

  return Object.entries(values).reduce<IGoalsDeliverablesLinkFilters[]>(
    (combinations, [key, value]) => combinations.flatMap((filters) => [filters, { ...filters, [key]: value }]),
    [{}],
  );
}

/**
 * Every filter combination the Deliverables tab has rows for, built from that tab's own rows: Goals counts don't
 * match them (tracking deliverables have no rows; a bonus content has rows under each of its groups).
 */
export function buildGoalsDeliverablesLinkKeys(rows: TGoalsDeliverablesLinkRow[]): Set<string> {
  const keys = new Set<string>();

  for (const row of rows) {
    if (!row.ctaTemplateId) {
      continue;
    }

    const scopes: TGoalsDeliverablesLinkScope[] = [
      ...(GOALS_COUNTED_MATCHING_TYPES.includes(row.matchingType) ? ['counted' as const] : []),
      ...(row.matchingType !== EMatchingType.unrelated ? ['group' as const] : []),
    ];

    for (const filters of getLinkFilterCombinations(row, row.ctaTemplateId)) {
      for (const scope of scopes) {
        keys.add(getGoalsDeliverablesLinkKey(scope, filters));
      }
    }
  }

  return keys;
}

export function getGoalsPlanTotal(rows: IGoalsRow[], lens: EGoalsLens): { complete: number; total: number } {
  return rows.reduce(
    (total, row) => ({
      complete: total.complete + row.planProgress[lens].complete,
      total: total.total + row.planProgress[lens].total,
    }),
    { complete: 0, total: 0 },
  );
}

function getProgressRatio(row: IGoalsRow, lens: EGoalsLens): number {
  const { complete, total } = row.planProgress[lens];

  return total > 0 ? complete / total : 1;
}

function getPlannedByCreator(
  shell: ICampaignAnalytics,
  filter: ICampaignAnalyticsFilter | null,
): Map<string, Map<string, IPlannedCtaApi>> {
  const plannedByCreator = new Map<string, Map<string, IPlannedCtaApi>>();
  const isVisible = (plannedCta: IPlannedCtaApi): boolean =>
    (!filter?.ctaTemplateIds.size || filter.ctaTemplateIds.has(plannedCta.ctaId)) &&
    (!filter?.platformFeatures.size || filter.platformFeatures.has(plannedCta.platformFeature));

  for (const creator of shell.creators.values()) {
    if (filter?.creatorIds.size && !filter.creatorIds.has(creator.id)) {
      continue;
    }

    const plannedCtas = new Map(
      Array.from(creator.plannedCtas.values())
        .filter(isVisible)
        .map((plannedCta) => [plannedCta.ctaId, plannedCta]),
    );

    if (plannedCtas.size > 0) {
      plannedByCreator.set(creator.id, plannedCtas);
    }
  }

  return plannedByCreator;
}

function getFirstPlannedCtaById(
  plannedByCreator: Map<string, Map<string, IPlannedCtaApi>>,
): Map<string, IPlannedCtaApi> {
  const firstPlannedCtaById = new Map<string, IPlannedCtaApi>();

  for (const plannedCtas of plannedByCreator.values()) {
    for (const [ctaId, plannedCta] of plannedCtas) {
      if (!firstPlannedCtaById.has(ctaId)) {
        firstPlannedCtaById.set(ctaId, plannedCta);
      }
    }
  }

  return firstPlannedCtaById;
}

function getNodeKey(creatorId: string, ctaId: string): string {
  return `${creatorId}::${ctaId}`;
}

function getOrderedColumnIds(
  ctaTemplates: ICtaTemplateApi[],
  plannedByCreator: Map<string, Map<string, IPlannedCtaApi>>,
): string[] {
  const plannedIds = new Set<string>();

  for (const plannedCtas of plannedByCreator.values()) {
    for (const ctaId of plannedCtas.keys()) {
      plannedIds.add(ctaId);
    }
  }

  const ordered = ctaTemplates.map((template) => template.id).filter((ctaId) => plannedIds.has(ctaId));
  const orderedSet = new Set(ordered);

  return [...ordered, ...Array.from(plannedIds).filter((ctaId) => !orderedSet.has(ctaId))];
}

function indexNodes(nodes: IAnalyticsNode[]): {
  evaluationsByKey: Map<string, IGoalsContentMatch[]>;
  matchesByKey: Map<string, IGoalsContentMatch[]>;
  placeholdersByKey: Map<string, IAnalyticsNode[]>;
} {
  const evaluationsByKey = new Map<string, IGoalsContentMatch[]>();
  const matchesByKey = new Map<string, IGoalsContentMatch[]>();
  const placeholdersByKey = new Map<string, IAnalyticsNode[]>();

  for (const node of nodes) {
    if (node.content) {
      const ctaResults = [...node.content.ctas.values()];

      for (const ctaResult of ctaResults) {
        const key = getNodeKey(node.creatorId, ctaResult.ctaId);

        evaluationsByKey.set(key, [...(evaluationsByKey.get(key) ?? []), { ctaResult, node }]);

        if (isCtaResultCounted(ctaResults, ctaResult)) {
          matchesByKey.set(key, [...(matchesByKey.get(key) ?? []), { ctaResult, node }]);
        }
      }

      continue;
    }

    if (node.nonExecutedCtaId) {
      const key = getNodeKey(node.creatorId, node.nonExecutedCtaId);
      placeholdersByKey.set(key, [...(placeholdersByKey.get(key) ?? []), node]);
    }
  }

  return { evaluationsByKey, matchesByKey, placeholdersByKey };
}

function getCellKind(plannedCta: IPlannedCtaApi | null): TGoalsCellKind {
  return plannedCta ? getFrequency(plannedCta.executions ?? []) : 'notInPlan';
}

function getFrequency(executions: IGoalsExecutionCount[]): TGoalsFrequency {
  const types = executions.map((execution) => execution.executionType);

  if (types.length === 0 || types.some((type) => !COUNTED_EXECUTION_EXCLUDED.has(type))) {
    return 'required';
  }

  return types.includes(EExecutionType.always) ? 'everyStream' : 'optional';
}

function getRequiredCount(executions: IGoalsExecutionCount[]): number {
  return executions
    .filter((execution) => !COUNTED_EXECUTION_EXCLUDED.has(execution.executionType))
    .reduce((sum, execution) => sum + (execution.executionCountTarget ?? 1), 0);
}

function getExecutionTime(node: IAnalyticsNode): string | null {
  return node.content?.socialContent?.executionTime ?? node.executionTime ?? null;
}

function getTime(value: string | null): number {
  return value ? new Date(value).getTime() : 0;
}

function byExecutionTime(left: IGoalsSlot, right: IGoalsSlot): number {
  return getTime(left.executionTime) - getTime(right.executionTime);
}

/** Splits the creator's contents of one group into required slots (chronological) and extras. */
function splitContents(
  params: IGoalsMatrixParams,
  kind: TGoalsCellKind,
  input: IGoalsCellInput,
): { counted: IGoalsSlot[]; extras: IGoalsSlot[]; required: number } {
  const matched = input.matches.filter((match) => match.ctaResult.matchingType === EMatchingType.matched);
  const bonus = input.matches.filter((match) => match.ctaResult.matchingType === EMatchingType.bonus);
  const toSlots = (matches: IGoalsContentMatch[]): IGoalsSlot[] =>
    matches.map((match) => buildCountedSlot(params, match));

  if (kind === 'everyStream') {
    const counted = toSlots(matched).sort(byExecutionTime);

    return { counted, extras: toSlots(bonus), required: counted.length };
  }

  if (kind !== 'required') {
    return { counted: [], extras: toSlots(bonus), required: 0 };
  }

  const isViaAlways = (match: IGoalsContentMatch): boolean =>
    match.ctaResult.plannedExecution?.executionType === EExecutionType.always;
  const fixedRequired = getRequiredCount(input.plannedCta?.executions ?? []);
  const fixedSlots = toSlots(matched.filter((match) => !isViaAlways(match))).sort(byExecutionTime);
  // Mixed templates: an "always" execution owes every stream, so its contents add a slot each (as Overview counts them).
  const alwaysSlots = toSlots(matched.filter(isViaAlways));

  // Matched contents beyond the target (manual matches) stay matched on Contents / Overview, so they fill blocks too.
  return {
    counted: [...fixedSlots, ...alwaysSlots].sort(byExecutionTime),
    extras: toSlots(bonus),
    required: fixedRequired + alwaysSlots.length,
  };
}

function buildCell(params: IGoalsMatrixParams, input: IGoalsCellInput): IGoalsCell {
  const kind = getCellKind(input.plannedCta);
  const { counted, extras, required } = splitContents(params, kind, input);
  const open = Math.max(0, required - counted.length);
  // One placeholder per execution slot still open, so one execution's over-target matches can't hide another's gap.
  const placeholders = kind === 'required' ? input.placeholders.slice(0, required) : [];
  // A narrowed date range shows only what lies inside it, like the other tabs: its contents and its planned placeholders.
  const free = params.isDateRangeNarrowed ? placeholders.length : Math.max(open, placeholders.length);
  const slots: IGoalsSlot[] = [
    ...counted,
    ...Array.from({ length: free }, (_, index): IGoalsSlot => buildEmptySlot('free', placeholders[index] ?? null)),
  ];
  const hasGoals = kind === 'required' || kind === 'everyStream';
  const kpiRows = hasGoals ? buildKpiRows(params, input.plannedCta, slots) : [];
  const deliverableRows = hasGoals ? buildDeliverableRows(params, input.plannedCta, slots) : [];

  return {
    counted: counted.length,
    creatorId: input.creatorId,
    ctaId: input.ctaId,
    ctaName: input.ctaName,
    deliverableRows,
    extraSlots: extras,
    free,
    kind,
    kpiRows,
    lenses: {
      [EGoalsLens.deliverables]: buildDeliverablesLens(params, slots, hasGoals),
      [EGoalsLens.kpis]: buildKpisLens(params, slots, hasGoals && kpiRows.length > 0),
    },
    optionalDelivered: kind === 'optional' ? input.evaluations.filter((match) => isDelivered(match)).length : 0,
    required: params.isDateRangeNarrowed && kind === 'required' ? counted.length + free : required,
    slots,
    subRowCount: Math.max(deliverableRows.length, kpiRows.length, 1),
  };
}

function isDelivered(match: IGoalsContentMatch): boolean {
  return match.ctaResult.matchingType === EMatchingType.matched || match.ctaResult.matchingType === EMatchingType.bonus;
}

function buildEmptySlot(kind: 'free', placeholder: IAnalyticsNode | null): IGoalsSlot {
  return {
    analyticsNode: placeholder,
    deliverables: [],
    executionTime: placeholder?.executionTime ?? null,
    hasKpiGoals: hasExecutionKpiGoals(placeholder?.plannedExecution, []),
    kind,
    kpis: [],
    kpiState: null,
    platformFeature: placeholder?.platformFeature ?? null,
    status: null,
  };
}

function buildCountedSlot(params: IGoalsMatrixParams, match: IGoalsContentMatch): IGoalsSlot {
  const { ctaResult, node } = match;
  const deliverables = getSlotDeliverables(params, ctaResult);
  const status = deliverables.length
    ? params.analyticsUtilities.getAnalyticsStatusWithStatusSet(new Set(deliverables.map((entry) => entry.status)))
    : (ctaResult.status ?? EAnalyticsStatus.processing);
  const kpis = getSlotKpis(match);

  return {
    analyticsNode: node,
    deliverables,
    executionTime: getExecutionTime(node),
    hasKpiGoals: hasExecutionKpiGoals(ctaResult.plannedExecution ?? node.plannedExecution, kpis),
    kind: 'counted',
    kpis,
    kpiState: getKpiState(kpis),
    platformFeature: node.platformFeature,
    status,
  };
}

/** Only a known execution without expected KPIs has no KPI goals; without one the slot keeps counting (as before). */
function hasExecutionKpiGoals(execution: IPlannedExecutionApi | null | undefined, kpis: IGoalsSlotKpi[]): boolean {
  return kpis.length > 0 || !execution || (execution.expectedKpis?.length ?? 0) > 0;
}

function getSlotDeliverables(params: IGoalsMatrixParams, ctaResult: ICtaResultApi): IGoalsSlotDeliverable[] {
  const { ctaAnalytics } = ctaResult;

  if (!ctaAnalytics) {
    return [];
  }

  return params.deliverableUtilities
    .getDeliverableCollectionConfigs(ctaAnalytics)
    .filter((config) => !params.deliverableUtilities.isTrackingDeliverable(config.type))
    .flatMap((config) =>
      (ctaAnalytics[config.collectionKey] ?? []).map(
        (entry): IGoalsSlotDeliverable => ({
          deliverableId: entry.deliverableId,
          name: entry.name,
          status: entry.statusManual ?? entry.status ?? EAnalyticsStatus.processing,
          type: config.type,
        }),
      ),
    );
}

function getSlotKpis(match: IGoalsContentMatch): IGoalsSlotKpi[] {
  const { ctaResult, node } = match;
  const expectedKpis: IExpectedKpiApi[] =
    ctaResult.plannedExecution?.expectedKpis ?? node.plannedExecution?.expectedKpis ?? [];
  const kpiAnalytics = node.content?.socialContent?.kpiAnalytics as IKpiAnalyticsApi | null | undefined;
  const isProcessing = node.content?.processStatus === EProcessStatus.processing;

  return expectedKpis.map((expectedKpi): IGoalsSlotKpi => {
    const value = isProcessing ? null : getKpiCurrentValue(kpiAnalytics, expectedKpi.kpi);
    const state = value === null ? 'noData' : value >= expectedKpi.expectedValue ? 'reached' : 'below';

    return {
      expectedKpiId: expectedKpi.expectedKpiId,
      key: getGoalsKpiKey(expectedKpi.kpi, expectedKpi.expectedValue),
      kpiType: expectedKpi.kpi,
      state,
      target: expectedKpi.expectedValue,
      value,
    };
  });
}

function getKpiState(kpis: IGoalsSlotKpi[]): TGoalsKpiState {
  const below = kpis.filter((kpi) => kpi.state === 'below').length;
  const noData = kpis.filter((kpi) => kpi.state === 'noData').length;

  if (kpis.length === 0 || noData === kpis.length) {
    return 'noData';
  }

  if (below === 0) {
    return noData > 0 ? 'noData' : 'reached';
  }

  return below === kpis.length ? 'below' : 'partial';
}

function isAllowed(allowed: EAnalyticsStatus[] | null | undefined, status: EAnalyticsStatus): boolean {
  return !allowed || allowed.includes(status);
}

function buildDeliverablesLens(params: IGoalsMatrixParams, slots: IGoalsSlot[], hasGoals: boolean): IGoalsCellLens {
  const allowed = params.exportConfig?.allowedContentStatuses;
  const counted = slots.filter((slot) => slot.kind === 'counted');
  const slotHidden = slots.map((slot) => slot.kind === 'counted' && !isAllowed(allowed, slot.status!));

  return {
    done: counted.length,
    hasGoals,
    isComplete:
      hasGoals && counted.length === slots.length && counted.every((slot) => slot.status === EAnalyticsStatus.ok),
    issues: slots.filter(
      (slot, index) =>
        !slotHidden[index] && (slot.status === EAnalyticsStatus.problem || slot.status === EAnalyticsStatus.failed),
    ).length,
    noData: 0,
    slotExcluded: slots.map(() => false),
    slotHidden,
    tones: slots.map((slot, index) => {
      if (slot.kind !== 'counted') {
        return 'empty';
      }

      return slotHidden[index] ? 'neutral' : STATUS_TONE[slot.status!];
    }),
  };
}

function buildKpisLens(params: IGoalsMatrixParams, slots: IGoalsSlot[], hasGoals: boolean): IGoalsCellLens {
  const allowed = params.exportConfig?.allowedKpiStatuses;
  const slotExcluded = slots.map((slot) => !slot.hasKpiGoals);
  const applicable = slots.filter((_, index) => !slotExcluded[index]);
  const counted = applicable.filter((slot) => slot.kind === 'counted');
  const slotHidden = slots.map(
    (slot, index) =>
      slot.kind === 'counted' && !slotExcluded[index] && !isAllowed(allowed, KPI_STATE_EXPORT_STATUS[slot.kpiState!]),
  );
  const isShown = (index: number): boolean => !slotHidden[index] && !slotExcluded[index];

  return {
    done: counted.filter((slot) => slot.kpiState === 'reached').length,
    hasGoals,
    isComplete:
      hasGoals && counted.length === applicable.length && counted.every((slot) => slot.kpiState === 'reached'),
    issues: slots.filter((slot, index) => isShown(index) && (slot.kpiState === 'partial' || slot.kpiState === 'below'))
      .length,
    noData: slots.filter((slot, index) => isShown(index) && slot.kpiState === 'noData').length,
    slotExcluded,
    slotHidden,
    tones: slots.map((slot, index) => {
      if (slot.kind !== 'counted') {
        return 'empty';
      }

      if (slotExcluded[index] || slotHidden[index]) {
        return 'neutral';
      }

      return KPI_STATE_TONE[slot.kpiState!];
    }),
  };
}

function buildDeliverableRows(
  params: IGoalsMatrixParams,
  plannedCta: IPlannedCtaApi | null,
  slots: IGoalsSlot[],
): IGoalsDeliverableRow[] {
  const allowed = params.exportConfig?.allowedDeliverableStatuses;
  const definitions = new Map<string, Pick<IGoalsSlotDeliverable, 'deliverableId' | 'name' | 'type'>>();

  if (plannedCta?.cta) {
    for (const entry of getSlotDeliverables(params, plannedCta.cta)) {
      definitions.set(entry.deliverableId, entry);
    }
  }

  for (const slot of slots) {
    for (const entry of slot.deliverables) {
      if (!definitions.has(entry.deliverableId)) {
        definitions.set(entry.deliverableId, entry);
      }
    }
  }

  const counted = slots.filter((slot) => slot.kind === 'counted');

  return Array.from(definitions.values()).map((definition): IGoalsDeliverableRow => {
    const statuses = slots.map(
      (slot) => slot.deliverables.find((entry) => entry.deliverableId === definition.deliverableId)?.status ?? null,
    );
    const hidden = statuses.map((status) => status !== null && !isAllowed(allowed, status));

    return {
      deliverableId: definition.deliverableId,
      fulfilled: statuses.filter((status) => status === EAnalyticsStatus.ok).length,
      hidden,
      labels: slots.map((slot, index) => {
        const status = statuses[index];

        if (slot.kind !== 'counted') {
          return NOT_DELIVERED_LABEL;
        }

        return status === null || hidden[index] ? null : DELIVERABLE_STATUS_LABEL[status];
      }),
      name: definition.name,
      tones: slots.map((slot, index) => {
        const status = statuses[index];

        if (slot.kind !== 'counted') {
          return 'empty';
        }

        if (status === null) {
          return 'empty';
        }

        return hidden[index] ? 'neutral' : STATUS_TONE[status];
      }),
      total: counted.length,
      type: definition.type,
    };
  });
}

function buildKpiRows(
  params: IGoalsMatrixParams,
  plannedCta: IPlannedCtaApi | null,
  slots: IGoalsSlot[],
): IGoalsKpiRow[] {
  const allowed = params.exportConfig?.allowedKpiStatuses;
  const definitions = new Map<string, Pick<IGoalsSlotKpi, 'expectedKpiId' | 'key' | 'kpiType' | 'target'>>();

  for (const execution of plannedCta?.executions ?? []) {
    for (const expectedKpi of execution.expectedKpis ?? []) {
      const key = getGoalsKpiKey(expectedKpi.kpi, expectedKpi.expectedValue);

      if (!definitions.has(key)) {
        definitions.set(key, {
          expectedKpiId: expectedKpi.expectedKpiId,
          key,
          kpiType: expectedKpi.kpi,
          target: expectedKpi.expectedValue,
        });
      }
    }
  }

  for (const kpi of slots.flatMap((slot) => slot.kpis)) {
    if (!definitions.has(kpi.key)) {
      definitions.set(kpi.key, kpi);
    }
  }

  return Array.from(definitions.values()).map((definition): IGoalsKpiRow => {
    const slotKpis = slots.map((slot) => slot.kpis.find((kpi) => kpi.key === definition.key) ?? null);
    const hidden = slotKpis.map((kpi) => !!kpi && !isAllowed(allowed, KPI_STATE_EXPORT_STATUS[kpi.state]));
    const values = slotKpis.map((kpi) => kpi?.value ?? null).filter((value): value is number => value !== null);

    return {
      average: values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null,
      expectedKpiId: definition.expectedKpiId,
      key: definition.key,
      kpiType: definition.kpiType,
      labels: slots.map((slot, index) => {
        const kpi = slotKpis[index];

        if (slot.kind !== 'counted') {
          return NOT_DELIVERED_LABEL;
        }

        return !kpi || hidden[index] ? null : KPI_TARGET_STATE_LABEL[kpi.state];
      }),
      reached: slotKpis.filter((kpi) => kpi?.state === 'reached').length,
      target: definition.target,
      tones: slots.map((slot, index) => {
        const kpi = slotKpis[index];

        if (slot.kind !== 'counted') {
          return 'empty';
        }

        if (!kpi) {
          return 'empty';
        }

        return hidden[index] ? 'neutral' : KPI_STATE_TONE[kpi.state];
      }),
      total: slotKpis.filter((kpi) => kpi !== null).length,
    };
  });
}

function getPlanProgress(cells: Record<string, IGoalsCell>, lens: EGoalsLens): { complete: number; total: number } {
  const lenses = Object.values(cells)
    .map((cell) => cell.lenses[lens])
    .filter((cellLens) => cellLens.hasGoals);

  return { complete: lenses.filter((cellLens) => cellLens.isComplete).length, total: lenses.length };
}

function getTemplateDeliverableCount(template: ICtaTemplateApi | null): number {
  if (!template) {
    return 0;
  }

  return TEMPLATE_DELIVERABLE_KEYS.filter((key) => !TRACKING_TEMPLATE_KEYS.has(key)).reduce(
    (sum, key) => sum + (template[key]?.length ?? 0),
    0,
  );
}

function getTemplateKpis(
  template: ICtaTemplateApi | null,
  fallbackPlannedCta: IPlannedCtaApi | null,
  cells: IGoalsCell[],
): IGoalsTemplateKpi[] {
  const expectedKpis = template
    ? (template.executions ?? []).flatMap((execution) => execution.expectedKpis ?? [])
    : (fallbackPlannedCta?.executions ?? []).flatMap((execution) => execution.expectedKpis ?? []);
  const kpis = new Map<string, IGoalsTemplateKpi>();

  for (const expectedKpi of expectedKpis) {
    if (expectedKpi.expectedValue === null || expectedKpi.expectedValue === undefined) {
      continue;
    }

    kpis.set(getGoalsKpiKey(expectedKpi.kpi, expectedKpi.expectedValue), {
      kpiType: expectedKpi.kpi,
      target: expectedKpi.expectedValue,
    });
  }

  for (const kpiRow of cells.flatMap((cell) => cell.kpiRows)) {
    if (!kpis.has(kpiRow.key)) {
      kpis.set(kpiRow.key, { kpiType: kpiRow.kpiType, target: kpiRow.target });
    }
  }

  return Array.from(kpis.values());
}

function buildColumn(
  params: IGoalsMatrixParams,
  ctaId: string,
  template: ICtaTemplateApi | null,
  fallbackPlannedCta: IPlannedCtaApi | null,
  rows: IGoalsRow[],
): IGoalsColumn {
  const executions: IGoalsExecutionCount[] = template?.executions ?? fallbackPlannedCta?.executions ?? [];
  const cells = rows.map((row) => row.cells[ctaId]);
  const templateKpis = getTemplateKpis(template, fallbackPlannedCta, cells);
  const frequency = getFrequency(executions);

  return {
    ctaId,
    // Templates can come without their deliverable lists (individual groups have none); the creators' plans carry
    // them. Optional cells build no rows, so they count from the plan itself.
    deliverableCount:
      getTemplateDeliverableCount(template) ||
      Math.max(0, ...cells.map((cell) => cell.deliverableRows.length)) ||
      (fallbackPlannedCta?.cta ? getSlotDeliverables(params, fallbackPlannedCta.cta).length : 0),
    footer: {
      [EGoalsLens.deliverables]: buildFooter(cells, EGoalsLens.deliverables),
      [EGoalsLens.kpis]: buildFooter(cells, EGoalsLens.kpis),
    },
    frequency,
    hasEveryStream:
      frequency === 'required' && executions.some((execution) => execution.executionType === EExecutionType.always),
    hasKpiGoals: templateKpis.length > 0,
    name: template?.name ?? fallbackPlannedCta?.cta?.name ?? '',
    requiredCount: getRequiredCount(executions),
    templateKpis,
  };
}

function buildFooter(cells: IGoalsCell[], lens: EGoalsLens): IGoalsFooter {
  const plannedCells = cells.filter((cell) => cell.lenses[lens].hasGoals);
  const tones = plannedCells.flatMap((cell) =>
    cell.lenses[lens].tones.filter((_, index) => !cell.lenses[lens].slotExcluded[index]),
  );
  const required = tones.length;
  const fulfilled = tones.filter((tone) => tone === 'success').length;
  const open = plannedCells.reduce(
    (sum, cell) =>
      sum + cell.slots.filter((slot, index) => slot.kind === 'free' && !cell.lenses[lens].slotExcluded[index]).length,
    0,
  );

  return {
    completeCreators: plannedCells.filter((cell) => cell.lenses[lens].isComplete).length,
    creatorsInPlan: plannedCells.length,
    fulfilled,
    open,
    percent: required > 0 ? Math.round((fulfilled / required) * 100) : 0,
    required,
    segments: FOOTER_TONES.map((tone) => ({ tone, value: tones.filter((slotTone) => slotTone === tone).length })),
  };
}
